import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Ticket, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitPublicTicket } from '../api/services/publicFormService';

type IssueType = 'Maintenance' | 'Plumbing' | 'Electrical' | 'Internet' | 'Cleanliness' | 'Security' | 'Other';

const ISSUE_TYPES: IssueType[] = [
  'Maintenance', 'Plumbing', 'Electrical', 'Internet', 'Cleanliness', 'Security', 'Other',
];

const PublicTicketForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('p') || '';

  const [roomNumber, setRoomNumber] = useState('');
  const [issueType, setIssueType] = useState<IssueType | ''>('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!propertyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center text-gray-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-400" />
          <p className="text-lg font-medium">Invalid link</p>
          <p className="text-sm mt-1">Please use the link sent by your host.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Ticket Submitted!</h2>
          <p className="text-gray-500 mb-6">Your host has been notified and will look into it shortly.</p>
          <button
            onClick={() => { setRoomNumber(''); setIssueType(''); setDescription(''); setStatus('idle'); }}
            className="text-sm text-green-600 underline"
          >
            Submit another ticket
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomNumber.trim() || !issueType || !description.trim()) return;

    setStatus('loading');
    setErrorMsg('');
    try {
      await submitPublicTicket({
        property_id: propertyId,
        room_number: roomNumber.trim(),
        message: `[${issueType}] ${description.trim()}`,
      });
      setStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-full mb-3">
            <Ticket className="h-7 w-7 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Raise a Ticket</h1>
          <p className="text-gray-500 text-sm mt-1">Report an issue to your PG owner</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          {/* Room Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Room Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 101"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50"
              required
            />
          </div>

          {/* Issue Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Issue Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {ISSUE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setIssueType(type)}
                  className={`rounded-xl py-2 px-2 text-xs font-medium border transition-colors ${
                    issueType === type
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Describe the issue <span className="text-red-500">*</span>
            </label>
            <textarea
              placeholder="Tell us what's wrong..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50 resize-none"
              required
            />
          </div>

          {/* Error */}
          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={status === 'loading' || !roomNumber || !issueType || !description}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Ticket'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">Powered by AffordaNest</p>
      </div>
    </div>
  );
};

export default PublicTicketForm;
